import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { TextFieldTheme } from "@wix/wix-ui-tpa/cssVars";
import { useState, useCallback } from "react";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { AutocompleteWUT } from "../autocomplete/autocomplete-wut.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { useInvokeCallback } from "../shared/use-invoke-callback.js";
const DropdownAutocompleteWUT = ({
  id,
  target,
  options,
  placeholder,
  required,
  hideLabel,
  value,
  disabled,
  description,
  hasError = false,
  errorMessage,
  error,
  label,
  onChange,
  onBlur,
  onFocus,
  FieldLayout,
  requiredIndicatorType,
  requiredIndicatorPlacement
}) => {
  const { labelId, inputId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
  const [inputValue, setInputValue] = useState(
    () => resolveLabel(options, value)
  );
  const invokeBlur = useInvokeCallback(onBlur);
  const handleBrowserAutofill = useCallback(
    (currentInputValue) => {
      const matchingOption = findMatchingOption(options, currentInputValue);
      if (matchingOption) {
        const newValue = String(matchingOption.id);
        onChange?.(newValue);
      }
    },
    [onChange, options]
  );
  const handleChange = useCallback(
    (event) => {
      handleBrowserAutofill(event.target.value);
      setInputValue(event.target.value);
    },
    [setInputValue, handleBrowserAutofill]
  );
  const handleSelect = useCallback(
    (option) => {
      const newValue = option.id;
      setInputValue(option.value);
      onChange(newValue);
    },
    [onChange, setInputValue]
  );
  const handleBlur = useCallback(() => {
    const currentLabel = resolveLabel(options, value);
    setInputValue(currentLabel);
    invokeBlur();
  }, [invokeBlur, value, options]);
  const handleManualInput = useCallback(() => {
    const matchingOption = findMatchingOption(options, inputValue);
    if (matchingOption) {
      const newValue = String(matchingOption.id);
      onChange?.(newValue);
      setInputValue(matchingOption.value);
    }
    if (!inputValue) {
      onChange?.("");
      setInputValue("");
    }
  }, [options, inputValue, onChange]);
  const handleInitialSelectedOptionsSet = useCallback(() => {
    const currentLabel = resolveLabel(options, value);
    setInputValue(currentLabel);
  }, [options, value]);
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          htmlFor: inputId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => /* @__PURE__ */ jsx(
        AutocompleteWUT,
        {
          id: inputId,
          value: inputValue,
          options,
          disabled,
          showArrow: true,
          placeholder,
          error: hasError,
          initialSelectedId: value ?? void 0,
          onInitialSelectedOptionsSet: handleInitialSelectedOptionsSet,
          onChange: handleChange,
          onSelect: handleSelect,
          onManualInput: handleManualInput,
          onBlur: handleBlur,
          onFocus,
          ariaDescribedBy,
          theme: TextFieldTheme.Box
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            target,
            hasError,
            errorMessage,
            id: errorId
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId })
      ] })
    }
  );
};
function resolveLabel(options, value) {
  if (!value) {
    return "";
  }
  return options.find((option) => option.id === value)?.value ?? "";
}
function findMatchingOption(options, label) {
  return options.find(
    (option) => option.value.toLocaleLowerCase() === label.toLocaleLowerCase().trim()
  );
}
export {
  DropdownAutocompleteWUT
};
//# sourceMappingURL=dropdown-autocomplete-wut.js.map
